import Button from '@mui/material/Button';
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { handleUnauthorizedResponse } from '../../api/http';
import PolicyApi from '../../api/PolicyApi';
import { AuthData } from '../../context/auth';
import { PolicyData, withPolicyContext } from '../../context/policy';
import { PRIMARY_BUTTON_STYLE } from '../../data/constants';
import { dayjs } from '../../date-util';
import { CancellationCreateRequest } from '../../types/policy';
import { getPolicyCharacteristics } from '../../util/util';
import DefaultDialog, { Action } from '../DefaultDialog';
import DropdownButtonItem from '../DropdownButtonItem';
import FieldLabel from '../fields/FieldLabel';
import { errorToast } from '../Toast';
import {
	getCategoryOptions,
	getTypeOptionsByCategory,
} from './Cancellations/util';
import ReactSelect, { SelectValue } from './fields/ReactSelect';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

const CancellationTypesModalTrigger = (props: Props) => {
	const { policyData, authData } = props;

	const [isOpen, setIsOpen] = useState(false);
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);
	const [selectedCategory, setSelectedCategory] = useState<SelectValue>({
		value: '',
		label: '',
	});
	const [selectedType, setSelectedType] = useState<SelectValue>({
		value: '',
		label: '',
	});

	const { productConfig, policy, setLoading } = policyData;
	const navigate = useNavigate();

	if (
		!productConfig ||
		!productConfig.policyConfiguration.cancellations ||
		!productConfig.policyConfiguration.cancellations.reasons
	) {
		return null;
	}

	const { categoryOptions, categoryTypeOptions } = getCategoryOptions(
		selectedCategory.value,
		productConfig,
	);
	const isCategoryEnabled = !_.isEmpty(categoryOptions);

	const getParams = () => {
		const characteristic = getPolicyCharacteristics(policy);
		const { startTimestamp, policyEndTimestamp } = characteristic;
		const todaysDate = new Date();
		todaysDate.setHours(0, 0, 0, 0);
		const todaysDateWithTZ = dayjs(todaysDate).tz(authData.auth.timezone);
		const todayTimestamp = todaysDateWithTZ.unix();
		const dateBefore = new Date(parseInt(policyEndTimestamp, 10));
		dateBefore.setDate(dateBefore.getDate() - 1);
		dateBefore.setHours(0, 0, 0, 0);
		const dateBeforeWithTZ = dayjs(dateBefore).tz(authData.auth.timezone);
		const dayBeforeEndTimestamp = dateBeforeWithTZ.unix();
		// Use todays timestamp if policystart date is in the past.
		let newEffectiveTimestamp =
			parseInt(startTimestamp, 10) <= todayTimestamp
				? `${todayTimestamp}`
				: startTimestamp;
		// Use day before end timestamp if policy end is equal to or before today.
		newEffectiveTimestamp =
			parseInt(policyEndTimestamp, 10) <= todayTimestamp
				? `${dayBeforeEndTimestamp}`
				: newEffectiveTimestamp;

		return {
			effectiveTimestamp: newEffectiveTimestamp,
		} as CancellationCreateRequest;
	};

	const handleCancelPolicyWithReason = (reasonName: string) => {
		const params: CancellationCreateRequest = getParams();
		params.name = reasonName;

		close();
		setLoading(true);
		PolicyApi.cancelPolicy(
			authData.auth.authorizationToken,
			policy.locator,
			params,
		)
			.then((cancellation) => {
				setLoading(false);
				if (cancellation && cancellation.data) {
					navigate(`cancellations/${cancellation.data.locator}`);
				}
			})
			.catch(async (err) => {
				if (await handleUnauthorizedResponse(err, authData)) {
					return;
				}
				errorToast('Unable to cancel policy');
				setLoading(false);
			});
	};
	const renderCancellationTypes = () => {
		if (
			productConfig.policyConfiguration.cancellations.reasons &&
			productConfig.policyConfiguration.cancellations.reasons.length
		) {
			const validReasons =
				productConfig.policyConfiguration.cancellations.reasons.filter(
					(reason: any) => reason.name !== 'lapse',
				);
			const onTypeChange = (opt: SelectValue) => setSelectedType(opt);

			if (!isCategoryEnabled) {
				const reasonButtons = validReasons.map((reason) => (
					<Button
						key={reason.name}
						style={PRIMARY_BUTTON_STYLE}
						className='button-primary'
						onClick={() => handleCancelPolicyWithReason(reason.name)}
					>
						{reason.title}
					</Button>
				));
				return (
					<>
						<p>Choose cancellation name:</p>
						{reasonButtons}
					</>
				);
			} else {
				return (
					<>
						<p>Choose cancellation category and name:</p>
						<div style={{ marginBottom: '8px' }}>
							<FieldLabel title='Cancellation Category' />
							<ReactSelect
								id='cancellation-category'
								options={categoryOptions}
								value={selectedCategory}
								onChange={(opt: SelectValue) => {
									setSelectedCategory(opt);
									const newTypeOptions = getTypeOptionsByCategory(
										opt.value,
										productConfig,
									);

									if (
										!newTypeOptions.find(
											(co) => co.value === selectedType.value,
										)
									) {
										onTypeChange({ value: '', label: '' }); // clear selected type if not included newly selected category
									}
								}}
							/>
						</div>
						<div>
							<FieldLabel title='Cancellation Name' />
							<ReactSelect
								id='cancellation-type'
								options={categoryTypeOptions}
								value={selectedType}
								onChange={onTypeChange}
							/>
						</div>
					</>
				);
			}
		} else {
			console.error('Unable to find cancellation types');
		}

		return null;
	};

	const actions: Action[] = [{ text: 'Close', onClick: close }];

	if (isCategoryEnabled) {
		const onCancelWithCategory = () => {
			const params: CancellationCreateRequest = getParams();
			params.name = selectedType.value;
			params.cancellationCategory = selectedCategory.value;

			close();
			setLoading(true);
			PolicyApi.cancelPolicy(
				authData.auth.authorizationToken,
				policy.locator,
				params,
			)
				.then((cancellation) => {
					setLoading(false);
					if (cancellation && cancellation.data) {
						navigate(`cancellations/${cancellation.data.locator}`);
					}
				})
				.catch(async (err) => {
					if (await handleUnauthorizedResponse(err, authData)) {
						return;
					}
					errorToast('Unable to cancel policy');
					setLoading(false);
				});
		};

		actions.push({
			text: 'Confirm',
			disabled: !selectedCategory?.value || !selectedType?.value,
			onClick: onCancelWithCategory,
		});
	}

	return (
		<>
			<DropdownButtonItem onClick={open} label='Cancel Policy' />
			<DefaultDialog
				actions={actions}
				open={isOpen}
				onClose={close}
				title='Cancel Policy'
			>
				<CancellationTypeWrapper>
					{renderCancellationTypes()}
				</CancellationTypeWrapper>
			</DefaultDialog>
		</>
	);
};

const CancellationTypeWrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-left: 30px;
	width: 80%;
`;

export default withPolicyContext<Props>(CancellationTypesModalTrigger);
